import {
  CheckableAvatarSelectItem,
  CheckableSelectItem,
  DropdownSelect,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo } from 'react';

import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import {
  ConversationsStaticFilterAttributes,
  ConversationsStaticFilterConfig,
  ConversationsStaticFilterType,
  ConversationsStaticFilterValue,
} from './conversationsStaticFilter.types';

/** The available static filters and their attributes. */
const staticFilters: Record<
  ConversationsStaticFilterType,
  ConversationsStaticFilterAttributes
> = {
  channel: {
    label: 'Channel',
    itemComponent: CheckableAvatarSelectItem,
  },
  direction: {
    label: 'Direction',
    itemComponent: CheckableSelectItem,
  },
  status: {
    label: 'Status',
    itemComponent: CheckableSelectItem,
  },
};

type ConversationsStaticFilterProps<
  TConversationsStaticFilterType extends ConversationsStaticFilterType,
> = {
  /** The type of the conversations static filter. */
  type: TConversationsStaticFilterType;
  /** The items to render. */
  items: readonly ConversationsStaticFilterConfig[TConversationsStaticFilterType][];
};

/** Represents the component for displaying a static conversations filter. */
export const ConversationsStaticFilter = <
  TConversationsStaticFilterType extends ConversationsStaticFilterType,
>({
  type,
  items,
}: ConversationsStaticFilterProps<TConversationsStaticFilterType>): JSX.Element => {
  const { itemComponent, label } = staticFilters[type];
  const { params, patchParams, unsetParams } =
    useAppSearchParams('/conversations');

  /**
   * The search param of the
   * {@link ConversationsStaticFilterType conversations static filter}.
   *
   * Note that we have to re-cast because we can't narrow down the values due to
   * the generic type. Additionally a union of arrays results in value being
   * typed as `never` because an empty array `[]` is technically `never[]`. So
   * instead of dealing with union of arrays we make it a single array of all
   * possible values from static filters, e.g. `'a'[] | 'b'[] -> ('a' |
   * 'b')[]`.
   */
  const selectedDropdownItemIds = useMemo<
    readonly ConversationsStaticFilterValue[]
  >(
    () =>
      typeof params[type] !== 'undefined'
        ? typeof params[type] === 'string'
          ? [params[type] as unknown as ConversationsStaticFilterValue]
          : (params[type] as readonly ConversationsStaticFilterValue[])
        : [],
    [params, type],
  );

  const handleChange = useCallback(
    (
      item: ConversationsStaticFilterConfig[TConversationsStaticFilterType],
      checked: boolean,
    ) => {
      patchParams({
        set: {
          [type]: checked
            ? [...selectedDropdownItemIds, item.id]
            : selectedDropdownItemIds.filter((id) => id !== item.id),
        },
        unset: ['limit', 'offset'],
      });

      ampli.conversationsFilterToggled({
        filter: type,
        value: item.id,
        checked,
      });
    },
    [patchParams, selectedDropdownItemIds, type],
  );

  const handleClear = useCallback(() => {
    unsetParams([type, 'limit', 'offset']);
    ampli.conversationsFilterCleared({ filter: type });
  }, [unsetParams, type]);

  return (
    <DropdownSelect
      keepOpen
      noArrow={false}
      // TODO: [TEAMS-9726] Remove re-casting when fixed in Habla
      items={
        items as ConversationsStaticFilterConfig[TConversationsStaticFilterType][]
      }
      selectedItems={
        selectedDropdownItemIds as ConversationsStaticFilterConfig[TConversationsStaticFilterType]['id'][]
      }
      itemComponent={itemComponent}
      buttonProps={{
        variant: 'tertiary',
        label: selectedDropdownItemIds.length || undefined,
      }}
      dropdownProps={{
        style: { minWidth: 280 },
      }}
      itemsDisplayCount={Math.min(items.length, 8)}
      onChange={handleChange}
      onClear={handleClear}
    >
      {label}
    </DropdownSelect>
  );
};
